import type { ModuleID, VersionString, ViewID } from "@bestowinc/tinkertoy-core";

import { recordPerformanceMetric } from "./recorder";
import { PERFORMANCE_METRIC_NAMES } from "./types";

export type ReadinessKey = string;

function makeReadinessKey(viewId: ViewID, viewVersion: VersionString): ReadinessKey {
  return `${viewId}:${viewVersion}`;
}

type ReadinessSession = {
  viewId: ViewID;
  viewVersion: VersionString;
  flowId?: string;
  requiredModules: Set<string>;
  readyModules: Set<string>;
  startTime: number;
  completed: boolean;
};

const sessions = new Map<ReadinessKey, ReadinessSession>();

function moduleKey(moduleId: ModuleID, version: VersionString): string {
  return `${moduleId}:${version}`;
}

export function beginV2ReadinessTracking(args: {
  viewId: ViewID;
  viewVersion: VersionString;
  flowId?: string;
  requiredModules: Array<{ moduleId: ModuleID; version: VersionString }>;
}): void {
  const key = makeReadinessKey(args.viewId, args.viewVersion);
  const required = new Set(
    args.requiredModules.map(({ moduleId, version }) => moduleKey(moduleId, version)),
  );

  if (required.size === 0) {
    recordPerformanceMetric(PERFORMANCE_METRIC_NAMES.v2Readiness, {
      durationMs: 0,
      outcome: "success",
      viewId: args.viewId,
      viewVersion: args.viewVersion,
      flowId: args.flowId,
    });
    return;
  }

  sessions.set(key, {
    viewId: args.viewId,
    viewVersion: args.viewVersion,
    flowId: args.flowId,
    requiredModules: required,
    readyModules: new Set(),
    startTime: performance.now(),
    completed: false,
  });
}

export function markModuleReadyForReadiness(args: {
  viewId: ViewID;
  viewVersion: VersionString;
  moduleId: ModuleID;
  version: VersionString;
}): void {
  const session = sessions.get(makeReadinessKey(args.viewId, args.viewVersion));
  if (!session || session.completed) {
    return;
  }

  const key = moduleKey(args.moduleId, args.version);
  if (!session.requiredModules.has(key)) {
    return;
  }

  session.readyModules.add(key);
  if (session.readyModules.size < session.requiredModules.size) {
    return;
  }

  session.completed = true;
  const durationMs = Math.max(0, performance.now() - session.startTime);
  recordPerformanceMetric(PERFORMANCE_METRIC_NAMES.v2Readiness, {
    durationMs,
    outcome: "success",
    viewId: session.viewId,
    viewVersion: session.viewVersion,
    flowId: session.flowId,
  });
  sessions.delete(makeReadinessKey(args.viewId, args.viewVersion));
}

export function cancelV2ReadinessTracking(
  viewId: ViewID,
  viewVersion: VersionString,
): void {
  const key = makeReadinessKey(viewId, viewVersion);
  const session = sessions.get(key);
  if (!session || session.completed) {
    sessions.delete(key);
    return;
  }

  const durationMs = Math.max(0, performance.now() - session.startTime);
  recordPerformanceMetric(PERFORMANCE_METRIC_NAMES.v2Readiness, {
    durationMs,
    outcome: "cancelled",
    viewId: session.viewId,
    viewVersion: session.viewVersion,
    flowId: session.flowId,
  });
  sessions.delete(key);
}
